import { createPortal } from 'react-dom';
import { useShallow } from 'zustand/react/shallow';
import { useConnectivityStore } from '@/api/connectivity';
import { connectivity } from '@/api/connectivity';

export function ReconnectOverlay() {
  const { state, shouldShowOverlay, pendingUnsafeRequests, slow, veryLong } = useConnectivityStore(
    useShallow((s) => ({
      state: s.state,
      shouldShowOverlay: s.shouldShowOverlay,
      pendingUnsafeRequests: s.pendingUnsafeRequests,
      slow: s.slow,
      veryLong: s.veryLong,
    })),
  );

  if (!shouldShowOverlay) return null;

  const isAwaitingDecision = state === 'awaiting_user_decision';

  return createPortal(
    <div
      role="alert"
      aria-live="polite"
      className="fixed inset-0 z-[9999] bg-black/30 backdrop-blur-sm flex items-center justify-center"
    >
      <div className="bg-white rounded-2xl shadow-xl px-6 py-5 max-w-sm mx-4 text-center">
        {isAwaitingDecision ? (
          <ReconnectedConfirm pendingCount={pendingUnsafeRequests} />
        ) : (
          <ProbingState slow={slow} veryLong={veryLong} />
        )}
      </div>
    </div>,
    document.body,
  );
}

function ProbingState({ slow, veryLong }: { slow: boolean; veryLong: boolean }) {
  return (
    <>
      <div
        className="inline-block w-8 h-8 border-4 border-td-blue/30 border-t-td-blue rounded-full animate-spin"
        role="status"
      >
        <span className="sr-only">Bezig met verbinden…</span>
      </div>
      <div className="mt-3 font-medium text-td-navy">Verbinding herstellen…</div>
      <div className="mt-1 text-sm text-gray-500">Uw werk is bewaard, even geduld.</div>
      {veryLong ? (
        <div className="mt-4 text-sm text-gray-600">
          Server is langere tijd onbereikbaar. Neem contact op.{' '}
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="text-td-blue underline"
          >
            Pagina vernieuwen
          </button>
        </div>
      ) : slow ? (
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-4 text-sm text-td-blue underline"
        >
          Pagina vernieuwen
        </button>
      ) : null}
    </>
  );
}

function ReconnectedConfirm({ pendingCount }: { pendingCount: number }) {
  const copy =
    pendingCount > 1
      ? `Uw vorige ${pendingCount} acties zijn mogelijk niet voltooid.`
      : 'Uw vorige actie is mogelijk niet voltooid.';

  return (
    <>
      <div className="font-medium text-td-navy">Verbinding hersteld</div>
      <div className="mt-1 text-sm text-gray-600">{copy}</div>
      <div className="mt-2 text-sm text-gray-600">Wilt u opnieuw proberen?</div>
      <div className="mt-4 flex gap-2 justify-center">
        <button
          type="button"
          onClick={() => connectivity.userClickRetry()}
          className="px-4 py-2 rounded-lg bg-td-blue text-white font-medium hover:bg-td-blue-dark"
          autoFocus
        >
          Opnieuw proberen
        </button>
        <button
          type="button"
          onClick={() => connectivity.userClickCancel()}
          className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 font-medium hover:bg-gray-200"
        >
          Annuleren
        </button>
      </div>
    </>
  );
}
